import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { supabase } from '~/lib/supabase';
import { passwordError, MIN_PASSWORD_LENGTH } from '~/lib/password';

export const Route = createFileRoute('/reset-password')({
  component: ResetPasswordScreen,
});

/**
 * /reset-password — handles the recovery-link callback.
 *
 * Supabase's createClient runs with detectSessionInUrl: true, so the access
 * token in the URL fragment is auto-exchanged for a session before this page
 * mounts. We then just need to capture the new password and call updateUser.
 * The route is intentionally outside _app so an expired/stale session can't
 * redirect away from it.
 */
function ResetPasswordScreen() {
  const nav = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [hasRecoverySession, setHasRecoverySession] = useState<boolean | null>(null);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setHasRecoverySession(!!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        setHasRecoverySession(!!s);
      }
    });
    return () => { mounted = false; sub.subscription.unsubscribe(); };
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null); setNotice(null);
    const pwErr = passwordError(password);
    if (pwErr) { setError(pwErr); return; }
    if (password !== confirm) {
      setError('Passwords don’t match.'); return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (error) { setError(error.message); return; }
    setNotice('Password updated. Redirecting…');
    setTimeout(() => nav({ to: '/' }), 900);
  }

  return (
    <div className="login-stage">
      <div className="login-equip eq-tl" aria-hidden><img src="/equipment/air-mover.png" alt="" /></div>
      <div className="login-equip eq-tr" aria-hidden><img src="/equipment/dehumidifier.png" alt="" /></div>
      <div className="login-equip eq-ml" aria-hidden><img src="/equipment/hydroxyl.png" alt="" /></div>
      <div className="login-equip eq-bl" aria-hidden><img src="/equipment/extractor.png" alt="" /></div>
      <div className="login-equip eq-br" aria-hidden><img src="/equipment/air-scrubber.png" alt="" /></div>
      <div className="login-equip eq-tc" aria-hidden><img src="/equipment/air-scrubber.png" alt="" /></div>
      <div className="login-equip eq-mr" aria-hidden><img src="/equipment/air-mover.png" alt="" /></div>
      <div className="login-equip eq-bc" aria-hidden><img src="/equipment/dehumidifier.png" alt="" /></div>

      <form className="login-min" onSubmit={onSubmit}>
        <div className="login-min-logo">
          <img
            src="https://www.pdqfirewaterdamage.com/images/pdq-restoration-logo.jpg"
            alt="PDQ Restoration — Fire & Water Cleanup"
          />
        </div>
        <h1 className="login-min-title">Set a new password</h1>
        <p className="login-min-sub">
          {hasRecoverySession === false
            ? 'Your reset link has expired or was already used. Request a new one from the sign-in page.'
            : `Pick something you’ll remember — minimum ${MIN_PASSWORD_LENGTH} characters.`}
        </p>

        <label className="login-field">
          <span>New password</span>
          <input className="login-inp" type="password" autoComplete="new-password" required
            value={password} onChange={(e) => setPassword(e.target.value)}
            disabled={hasRecoverySession === false} />
        </label>
        <label className="login-field">
          <span>Confirm password</span>
          <input className="login-inp" type="password" autoComplete="new-password" required
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
            disabled={hasRecoverySession === false} />
        </label>

        {error && <div className="login-err">{error}</div>}
        {notice && <div className="login-notice">{notice}</div>}

        <button type="submit" className="login-btn"
          disabled={submitting || hasRecoverySession === false}>
          {submitting ? 'Saving…' : 'Save password'}
        </button>

        <button
          type="button"
          className="login-link"
          onClick={() => nav({ to: '/login' })}
        >
          Back to sign in
        </button>

        <p className="login-foot">PDQ Restoration &middot; Fire &amp; Water Cleanup</p>
      </form>
    </div>
  );
}
